import React, { useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import authService from "@/services/AuthService";
import { getClientConfig } from "@/services/clientConfig";
import {
  PRESET_AVATAR_IDS,
  buildPresetAvatarUrl,
  extractPresetAvatarId,
  isGenericDefaultPicture
} from "@/constants/presetAvatars";
import { GhostButton, IconButton } from "@/components/ds";

interface Props {
  /** Current WorkspaceUser.Picture URL (may be a preset, an uploaded photo, or the generic default). */
  currentPictureUrl: string | null | undefined;
  onSaved(pictureUrl: string): void;
  onClose(): void;
}

/**
 * Preset-avatar picker for velaro-admin, shown as a pop-out from the profile
 * menu (same shell pattern as TimezoneModal.tsx).
 *
 * Reuse decision: this reads/writes WorkspaceUser.Picture — the SAME field
 * velaro-messaging's own avatar picker (ProfileController.SelectPresetAvatar /
 * ResetProfilePicture) already reads/writes, validated server-side there. Since
 * admin's api.messaging client already authenticates directly against that API
 * with the same JWT (see ProfileProvider.tsx's Profile/Me call, which is core
 * login infra), this component calls those existing endpoints directly instead
 * of duplicating WorkspaceUser-resolution + validation logic in a new admin
 * backend controller. No new velaro-admin server code is needed for this
 * feature — see docs note in presetAvatars.ts.
 */
export default function AvatarPickerModal({
  currentPictureUrl,
  onSaved,
  onClose
}: Props) {
  const api = useApi();
  const ref = useRef<HTMLDivElement>(null);
  const messagingClientUrl = getClientConfig().messagingClientUrl;

  const [selectedId, setSelectedId] = useState<string | null>(
    extractPresetAvatarId(currentPictureUrl)
  );
  const [saving, setSaving] = useState<string | null>(null); // avatar id (or "reset") currently saving
  const [error, setError] = useState<string | null>(null);

  const showingGenericDefault =
    isGenericDefaultPicture(currentPictureUrl) && !selectedId;

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [onClose]);

  async function selectPreset(id: string) {
    if (saving) return;
    setSaving(id);
    setError(null);
    try {
      const res = await api.messaging.post("Profile/SelectPresetAvatar", {
        AvatarId: id
      });
      if (!res.ok) {
        setError(`Failed to save avatar (HTTP ${res.status})`);
        return;
      }
      const pictureUrl = await res.json();
      setSelectedId(id);
      authService.updateProfilePicture(pictureUrl);
      onSaved(pictureUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  async function resetToDefault() {
    if (saving) return;
    setSaving("reset");
    setError(null);
    try {
      const res = await api.messaging.post("Profile/ResetProfilePicture", {});
      if (!res.ok) {
        setError(`Failed to reset avatar (HTTP ${res.status})`);
        return;
      }
      const pictureUrl = await res.json();
      setSelectedId(null);
      authService.updateProfilePicture(pictureUrl);
      onSaved(pictureUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      <style>{`
        @keyframes v-avatar-picker-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .v-avatar-picker-modal { animation: v-avatar-picker-in 180ms ease-out both; }
      `}</style>
      <div
        ref={ref}
        className="v-avatar-picker-modal absolute bottom-0 left-full ml-2 z-30 w-80 rounded-xl shadow-xl border border-zinc-200 bg-white overflow-hidden"
        aria-label="Choose an avatar"
      >
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-700">
            Choose an avatar
          </span>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            variant="ghost"
            size="sm"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </IconButton>
        </div>

        <div className="p-4 space-y-3">
          {showingGenericDefault && (
            <div className="rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2 text-[11px] text-zinc-500">
              No avatar selected yet — pick one below.
            </div>
          )}

          <div
            className="grid grid-cols-6 gap-2 max-h-56 overflow-y-auto pr-1"
            role="listbox"
            aria-label="Preset avatars"
          >
            {PRESET_AVATAR_IDS.map((id) => {
              const url = buildPresetAvatarUrl(messagingClientUrl, id);
              const active = selectedId === id;
              const isSaving = saving === id;
              return (
                <button
                  key={id}
                  role="option"
                  aria-selected={active}
                  aria-label={`Avatar ${id}`}
                  title={id}
                  disabled={!!saving}
                  onClick={() => selectPreset(id)}
                  className={`relative w-10 h-10 rounded-full overflow-hidden border-2 transition-colors disabled:opacity-60 ${
                    active
                      ? "border-(--ds-primary)"
                      : "border-transparent hover:border-zinc-300"
                  }`}
                >
                  <img
                    src={url}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {isSaving && (
                    <span className="absolute inset-0 bg-white/60 flex items-center justify-center">
                      <span className="w-3 h-3 rounded-full border-2 border-(--ds-primary) border-t-transparent animate-spin" />
                    </span>
                  )}
                  {active && !isSaving && (
                    <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-(--ds-primary) flex items-center justify-center ring-1 ring-white">
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 20 20"
                        fill="white"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <GhostButton
            onClick={resetToDefault}
            disabled={!!saving}
            className="!text-[11px]"
          >
            Reset to default
          </GhostButton>

          {error && <div className="text-[11px] text-red-500">{error}</div>}
        </div>
      </div>
    </>
  );
}
